import React, { useEffect, useState, useMemo, useContext, useRef } from "react";
import { useParams } from "react-router-dom";
import Component from "../components/Component";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { DragHandleProvider } from "../../editor/context/DragHandleContext";
import { makeSortableId } from "../../editor/utils/reorderCache";
import Background from "../components/Background";
import NoComponentPage from "../utils/NoComponentPage";
import LoadingPage from "../utils/LoadingPage";
import ErrorWebsite from "./ErrorWebsite";
import { ComponentSkeleton } from "../components/SkeletonPlaceholder";
import { usePageSkeleton } from '../../core/hooks/useDataFetching';
import useSpacing from '../../core/hooks/useSpacing';
import useBackground from '../../core/hooks/useBackground';
import { getBreakpoint } from '../../core/utils/pageUtils';
import AddButtonWithIndicator from "../../editor/components/AddButtonWithIndicator";
import AddButtonOverlay from "../../editor/add-buttons/AddButtonOverlay";
import { useAddElement } from "../../editor/context/AddElementContext";
import { EditContext } from "../../editor/context/EditContext";
import PreviewContext, { usePreviewOptional } from "../../editor/context/PreviewContext";
import { TUTORIAL_EVENTS, TUTORIAL_TARGETS } from "../../editor/tutorial/constants";
import useDevRenderLoopGuard from "../../core/hooks/useDevRenderLoopGuard";

const MAXWIDTH_CENTERING_DEBUG = "MAXWIDTH_CENTERING_DEBUG";
const __isDev = process.env.NODE_ENV === "development";

function sortComponents(components = []) {
  return (components || [])
    .filter(Boolean)
    .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
}

function groupComponents(components, layoutList) {
  if (!Array.isArray(layoutList) || layoutList.length === 0) {
    return components.map((c) => [c]);
  }
  const rows = [];
  let i = 0;
  for (const count of layoutList) {
    if (i >= components.length) break;
    if (count > 0) {
      rows.push(components.slice(i, i + count));
      i += count;
    }
  }
  if (i < components.length) {
    rows.push(components.slice(i));
  }
  return rows;
}

const DEFAULT_PAGE_MAX_WIDTH = 1600;
const DEFAULT_MIN_WIDTH = "250px";
const ROW_STYLE = { width: "100%" };
const getCellStyle = (count) => ({
  width: `${100 / count}%`,
  minWidth: DEFAULT_MIN_WIDTH,
});

const SortableWrapper = ({ id, data, children }) => {
  // dnd-kit treats `data` as an input to its internal registration effects.
  // Passing a new object each render can cause re-registration churn and, in some cases,
  // "Maximum update depth exceeded" loops. Stabilize by memoizing by value.
  const stableData = useMemo(() => data, [JSON.stringify(data ?? null)]);
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id, data: stableData });
  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <DragHandleProvider value={{ dragListeners: listeners }}>
        {children}
      </DragHandleProvider>
    </div>
  );
};

const ContentRenderer = ({ rows, components, className, isLoading, pageId }) => {

  // Declare droppable and query client before any early return
  const droppableId = `parent:page:${pageId || "unknown"}`;
  const { setNodeRef } = useDroppable({ id: droppableId });
  const { openAddModal } = useAddElement();
  const editContext = useContext(EditContext);
  const isEditMode = !!editContext;
  const notifyTutorialAddButtonClick = () => {
    try {
      window.dispatchEvent(
        new CustomEvent(TUTORIAL_EVENTS.ADD_BUTTON_CLICKED, {
          detail: { source: "website-page", pageId },
        }),
      );
    } catch (_) {}
  };

  // IMPORTANT: keep the `items` array referentially stable when IDs/order are unchanged.
  // dnd-kit uses this as an input to internal effects; recreating it every render can cause
  // registration churn and, in some cases, "Maximum update depth exceeded" loops.
  //
  // NOTE: This must be declared before any early returns to satisfy React hooks rules.
  const itemsSignature = (components || []).map((c) => c?.id).join("|");
  const itemsIds = useMemo(
    () => (components || []).map((c) => makeSortableId({ type: "component", id: c.id })),
    [itemsSignature],
  );

  // Show skeleton placeholders while loading
  if (isLoading) {
    return (
      <div ref={setNodeRef} id={droppableId} className={className}>
        <div className="space-y-6">
          <ComponentSkeleton />
          <ComponentSkeleton />
          <ComponentSkeleton />
        </div>
      </div>
    );
  }

  return (
      <div ref={setNodeRef} className={className} id={droppableId}>
        <SortableContext items={itemsIds} strategy={verticalListSortingStrategy}>
          {rows ? (
            rows.map((row, ri) => (
                <div
                  key={ri}
                  className="flex flex-wrap justify-center m-0 p-0 relative group"
                  style={ROW_STYLE}
                >
                  {row.map((comp, idx) => (
                    <div key={comp.id} className="p-0 m-0" style={getCellStyle(row.length)}>
                      <SortableWrapper
                        id={makeSortableId({ type: "component", id: comp.id })}
                        data={{ parentType: "page", parentId: pageId, kind: "component", index: idx }}
                      >
                        <Component componentId={comp.id} />
                      </SortableWrapper>
                    </div>
                  ))}
                  {/* Add button before first row */}
                  {isEditMode && ri === 0 && (
                    <AddButtonOverlay
                      layer="page-row-before"
                      overlayId={`page-row-before-${pageId}-${ri}`}
                      variant="page"
                      className="absolute top-0 left-0 w-full -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none group-hover:pointer-events-auto focus-within:pointer-events-auto"
                    >
                      <AddButtonWithIndicator
                        tutorialTargetId={TUTORIAL_TARGETS.ADD_BUTTON}
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          notifyTutorialAddButtonClick();
                          console.log('🔍 ADDELEM_CREATE_ERROR - WebsitePage AddButton clicked before first row (row layout):', {
                            pageId,
                            rowIndex: 0,
                            order: 0,
                          });
                          openAddModal({ 
                            pageId, 
                            order: 0,
                          }); 
                        }}
                      />
                    </AddButtonOverlay>
                  )}
                  {/* Add button after each row */}
                  {isEditMode && (
                    <AddButtonOverlay
                      layer="page-row-after"
                      overlayId={`page-row-after-${pageId}-${ri}`}
                      variant="page"
                      shift={ri === rows.length - 1 ? "right" : "none"}
                      className="absolute bottom-0 left-0 w-full translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none group-hover:pointer-events-auto focus-within:pointer-events-auto"
                    >
                      <AddButtonWithIndicator
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          notifyTutorialAddButtonClick();
                          console.log('🔍 ADDELEM_CREATE_ERROR - WebsitePage AddButton clicked after row (row layout):', {
                            pageId,
                            rowIndex: ri,
                            order: ri + 1,
                          });
                          openAddModal({ 
                            pageId, 
                            order: ri + 1,
                          }); 
                        }}
                      />
                    </AddButtonOverlay>
                  )}
                </div>
            ))
          ) : (
            components.map((comp, idx) => (
              <div key={comp.id} className="relative group">
                <SortableWrapper
                  id={makeSortableId({ type: "component", id: comp.id })}
                  data={{ parentType: "page", parentId: pageId, kind: "component", index: idx }}
                >
                  <div className="m-0 p-0">
                    <Component componentId={comp.id} />
                  </div>
                </SortableWrapper>
                {/* Add button before first component */}
                {isEditMode && idx === 0 && (
                  <AddButtonOverlay
                    layer="page-linear-before"
                    overlayId={`page-linear-before-${pageId}-${comp.id}`}
                    variant="page"
                    className="absolute top-0 left-0 w-full -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none group-hover:pointer-events-auto focus-within:pointer-events-auto"
                  >
                    <AddButtonWithIndicator
                      tutorialTargetId={TUTORIAL_TARGETS.ADD_BUTTON}
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        notifyTutorialAddButtonClick();
                        console.log('🔍 ADDELEM_CREATE_ERROR - WebsitePage AddButton clicked before first component (linear layout):', {
                          pageId,
                          compIndex: 0,
                          order: 0,
                        });
                        openAddModal({ 
                          pageId, 
                          order: 0,
                        }); 
                      }}
                    />
                  </AddButtonOverlay>
                )}
                {/* Add button after each component */}
                {isEditMode && (
                  <AddButtonOverlay
                    layer="page-linear-after"
                    overlayId={`page-linear-after-${pageId}-${comp.id}-${idx}`}
                    variant="page"
                    shift={idx === components.length - 1 ? "right" : "none"}
                    className="absolute bottom-0 left-0 w-full translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none group-hover:pointer-events-auto focus-within:pointer-events-auto"
                  >
                    <AddButtonWithIndicator
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        notifyTutorialAddButtonClick();
                        console.log('🔍 ADDELEM_CREATE_ERROR - WebsitePage AddButton clicked after component (linear layout):', {
                          pageId,
                          compIndex: idx,
                          order: idx + 1,
                        });
                        openAddModal({ 
                          pageId, 
                          order: idx + 1,
                        }); 
                      }}
                    />
                  </AddButtonOverlay>
                )}
              </div>
            ))
          )}
        </SortableContext>
      </div>
  );
};

const WebsitePage = ({ websiteId, domain, subdomain: subdomainOverride }) => {
  const { subdomain: routeSubdomain } = useParams();
  const subdomain = subdomainOverride ?? routeSubdomain;
  const debugOnceRef = useRef({ lastKey: null });
  const isEditMode = !!useContext(EditContext);

  useDevRenderLoopGuard("WebsitePage");
  
  // Get containerWidth from PreviewContext (edit mode) or use window width (public routes)
  const preview = usePreviewOptional();
  const { containerWidth: contextWidth } = preview || {};
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  
  useEffect(() => {
    // Only set up window resize listener if we don't have context width
    if (contextWidth !== undefined) return;
    if (typeof window === 'undefined') return;
    
    const handleResize = () => {
      const next = window.innerWidth;
      setWindowWidth((prev) => (prev === next ? prev : next));
    };
    handleResize(); // Get initial value
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [contextWidth]);
  
  const containerWidth = contextWidth ?? windowWidth;

  // Use new skeleton hook for lightweight page data
  const {
    data: page,
    isLoading,
    error,
  } = usePageSkeleton(websiteId, subdomain);

  const resolvedPageMaxWidth = useMemo(() => {
    const raw = page?.max_width;
    // Explicit null means "Unlimited" (no max-width cap).
    if (raw === null) return null;
    const n =
      typeof raw === "number"
        ? raw
        : typeof raw === "string"
          ? parseInt(raw, 10)
          : Number.NaN;
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_PAGE_MAX_WIDTH;
  }, [page?.max_width]);

  // When a page has a max-width constraint, responsive layout/padding decisions should be based
  // on the *effective* rendered width, not the full window width. Otherwise, very wide screens
  // can incorrectly pick "extra_large" breakpoint settings even though the content is capped.
  const effectiveContentWidth = useMemo(() => {
    if (resolvedPageMaxWidth === null) return containerWidth;
    return Math.min(containerWidth, resolvedPageMaxWidth);
  }, [containerWidth, resolvedPageMaxWidth]);

  // Breakpoint is derived from the effective content width; keep it computed to avoid state/effect update loops.
  const breakpoint = useMemo(
    () => getBreakpoint(effectiveContentWidth),
    [effectiveContentWidth],
  );

  // Derived sorted components and row grouping - always called before any returns
  const sortedComponents = useMemo(() => {
    const componentsList = page?.components || [];
    console.log('[IMAGE_DELETE_FLOW] WebsitePage rendering with components:', {
      pageId: page?.id,
      pageSubdomain: page?.subdomain || 'main',
      componentCount: componentsList.length,
      componentIds: componentsList.map(c => c.id)
    });
    const components = sortComponents(componentsList);
    return components;
  }, [page?.components, page?.id, page?.subdomain]);

  // Robust layoutList: parse if string, use as is if array
  const layoutList = useMemo(() => {
    // New structure: page.layout is a LayoutByLayout object with breakpoint fields
    const raw = page?.layout?.[breakpoint];
    
    // If it's already an array, use it directly
    if (Array.isArray(raw)) {
      return raw.length > 0 ? raw : null;
    }
    
    // Fallback: handle string for backward compatibility during migration
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
      } catch {
        // Legacy comma-separated values
        const parts = raw.split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => !isNaN(n) && n > 0);
        return parts.length > 0 ? parts : null;
      }
    }
    
    return raw;
  }, [page?.layout, breakpoint]);

  const rows = useMemo(
    () =>
      sortedComponents.length > 0 && Array.isArray(layoutList)
        ? groupComponents(sortedComponents, layoutList)
        : null,
    [sortedComponents, layoutList],
  );
  const hasContent = sortedComponents.length > 0;

  const {
    bgImageUrl,
    imageStyleClasses,
    imageInlineStyles,
    bgPatternClass,
    bgPatternStyle,
    bgPatternJsLayer,
  } = useBackground(page?.background || {});
  const { padding: pagePaddingClasses } = useSpacing(
    page || {},
    null,
    8,
    true,
    false,
    false,
    effectiveContentWidth,
  );
  const pageContentClassName = useMemo(
    () => [pagePaddingClasses, "mx-auto"].filter(Boolean).join(" "),
    [pagePaddingClasses],
  );

  // ---------------------------------------------------------------------------
  // Debug logs for centering issues (dev-only)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!__isDev) return;
    try {
      const key = [
        "wp",
        websiteId ?? "no-site",
        subdomain ?? "main",
        page?.id ?? "no-page",
        resolvedPageMaxWidth ?? "no-max",
        effectiveContentWidth ?? "no-eff",
        breakpoint ?? "no-bp",
      ].join("|");
      if (debugOnceRef.current.lastKey === key) return;
      debugOnceRef.current.lastKey = key;

      console.log(MAXWIDTH_CENTERING_DEBUG, "WebsitePage state", {
        websiteId,
        subdomain,
        pageId: page?.id,
        pageMaxWidth: resolvedPageMaxWidth,
        effectiveContentWidth,
        containerWidth,
        contextWidth,
        windowWidth,
        breakpoint,
        pagePaddingClasses,
        pageContentClassName,
        hasPreviewContext: Boolean(preview),
      });
    } catch (_) {}
  }, [
    websiteId,
    subdomain,
    page?.id,
    resolvedPageMaxWidth,
    effectiveContentWidth,
    containerWidth,
    contextWidth,
    windowWidth,
    breakpoint,
    pagePaddingClasses,
    pageContentClassName,
    preview,
  ]);

  useEffect(() => {
    if (!__isDev) return;
    if (typeof window === "undefined") return;

    const measure = () => {
      try {
        const shell = document.querySelector('[data-page-shell="true"]');
        const content = document.querySelector('[data-page-content="true"]');
        const root = document.getElementById("root");
        if (!shell || !content) return;

        const sr = shell.getBoundingClientRect();
        const cr = content.getBoundingClientRect();
        const cs = window.getComputedStyle(content);

        const leftSpace = cr.left - sr.left;
        const rightSpace = sr.right - cr.right;
        const shellCenter = sr.left + sr.width / 2;
        const contentCenter = cr.left + cr.width / 2;

        const componentRects = Array.from(document.querySelectorAll(".component-box"))
          .slice(0, 6)
          .map((el, idx) => {
            const r = el.getBoundingClientRect();
            return {
              idx,
              left: Math.round(r.left),
              right: Math.round(r.right),
              width: Math.round(r.width),
              centerDeltaFromContent: Math.round((r.left + r.width / 2) - contentCenter),
            };
          });

        console.log(MAXWIDTH_CENTERING_DEBUG, "WebsitePage rects", {
          windowInnerWidth: window.innerWidth,
          docClientWidth: document.documentElement?.clientWidth,
          bodyClientWidth: document.body?.clientWidth,
          rootClientWidth: root?.clientWidth,
          bodyScrollWidth: document.body?.scrollWidth,
          shell: {
            left: Math.round(sr.left),
            right: Math.round(sr.right),
            width: Math.round(sr.width),
          },
          content: {
            left: Math.round(cr.left),
            right: Math.round(cr.right),
            width: Math.round(cr.width),
          },
          spaces: {
            leftSpace: Math.round(leftSpace),
            rightSpace: Math.round(rightSpace),
            spaceDelta: Math.round(leftSpace - rightSpace),
            centerDelta: Math.round(contentCenter - shellCenter),
          },
          contentStyle: {
            maxWidth: cs.maxWidth,
            width: cs.width,
            marginLeft: cs.marginLeft,
            marginRight: cs.marginRight,
            paddingLeft: cs.paddingLeft,
            paddingRight: cs.paddingRight,
          },
          componentRects,
        });
      } catch (_) {}
    };

    const raf = window.requestAnimationFrame(measure);
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    return () => {
      try { window.cancelAnimationFrame(raf); } catch (_) {}
      window.removeEventListener("resize", onResize);
    };
  }, [page?.id, breakpoint, pagePaddingClasses, pageContentClassName, resolvedPageMaxWidth, effectiveContentWidth]);

  if (error) return <ErrorWebsite domain={domain} showFooter={false} />;
  if (isLoading) return <LoadingPage />;

  const pageShell = (
    <div className={`relative ${isEditMode ? '' : 'z-0'} flex flex-col min-h-full bg-white`} data-page-shell="true">
      <Background
        bgImage={bgImageUrl}
        imageStyleClasses={imageStyleClasses}
        imageInlineStyles={imageInlineStyles}
        colorClass={bgPatternClass}
        colorStyle={bgPatternStyle}
        jsPatternLayer={bgPatternJsLayer}
      />
      <div className={`relative ${isEditMode ? '' : 'z-10'} flex-grow`}>
        <div
          data-page-content="true"
          className={pageContentClassName}
          style={{
            maxWidth: resolvedPageMaxWidth ?? undefined,
            width: "100%",
          }}
        >
          {!hasContent ? (
            <NoComponentPage
              name={page.name}
              domain={domain}
              subdomain={subdomain}
            />
          ) : (
            <ContentRenderer
              rows={rows}
              components={sortedComponents}
              className="w-full"
              isLoading={isLoading}
              pageId={page?.id}
            />
          )}
        </div>
      </div>
    </div>
  );

  // On public routes we don't have a PreviewProvider. Provide a minimal PreviewContext so
  // responsive hooks (spacing/layout) can base calculations on the effective content width
  // (especially when max_width is set), keeping the layout visually centered and consistent.
  if (!preview) {
    return (
      <PreviewContext.Provider
        value={{
          mode: "responsive",
          viewport: null,
          containerWidth: effectiveContentWidth,
          contentWidth: effectiveContentWidth,
        }}
      >
        {pageShell}
      </PreviewContext.Provider>
    );
  }

  return pageShell;
};

export default React.memo(WebsitePage);
